// 수비 화면의 파생 — **화면이 자기 계산을 한 벌 더 갖지 않게 하는 자리**
//
// ── 이 모듈이 왜 화면 밖에 있는가 ───────────────────────────────────────────
//
// 수비 화면이 그리려는 것 — 남은 rallying 이 흐르다 행군으로 넘어가 착탄 T− 로 이어지는
// 줄, 칩에 박히는 `R 1:42.0`·`M 0:22.0`, 그리고 불가 판정 — 은 전부 「여기서 한 줄 빼면
// 되잖아」로 보이는 모양이다. 화면이 그 한 줄을 자기가 쓰는 순간 **δ 의 부호가 두 곳에
// 살고**, 코어에 붙어 있는 수비 단위 검사 스물일곱 개는 그 두 번째 벌을 한 글자도 지키지
// 못한다. 그리고 그 어긋남은 화면에 **그럴듯한 숫자로** 뜬다 — 아무도 못 본다.
//
// 그래서 규율을 하나 세운다. **이 모듈은 착탄 역산도 보낼 시점도 트리거도 불가 판정도
// 직접 쓰지 않고 `src/core/timing/defense.ts` 를 부르기만 한다.** 코어가 낸 판정 객체를
// 통째로 들고 다니고 필드를 다시 조립하지 않는다. `tests/unit/defense/derive.test.ts` 가
// 이 모듈의 산출을 코어 직접 호출과 **전량으로** 견주어 그 규율을 기계로 붙든다 — 필드를
// 골라 비교하면 고르지 않은 필드가 조용히 갈리고, 재구성 스텝이 2026-08-15 에 같은 함정을
// 응답 전량 비교로 막았다.
//
// ── 왜 `screens/` 밖인가 ────────────────────────────────────────────────────
//
// 이 파생을 읽는 화면이 둘이다 — 작전 구성의 defense 탭(착탄순 시퀀스)과 defense 열람
// (roster 아코디언). 둘 중 한쪽 디렉터리에 두면 다른 쪽이 **형제 화면 안으로** import 하게
// 되고, 그러면 화면 하나를 되돌릴 때 다른 화면이 함께 무너진다. 공격 열람 스텝이 신호 선택
// 규칙을 `src/web/signal/selection.ts` 로 화면 밖에 둔 것과 같은 자리다. 이 파일은 계획서의
// 「건드리는 파일」 목록 밖에서 생겼으므로 그 사실을 핸드오프에 적는다.
//
// ── 흐르는 시각을 여기서 형식화하지 않는다 ──────────────────────────────────
//
// 0.1초 칸으로 버리는 규칙은 이미 두 곳에 있고(열람 화면의 `live-clock.ts`, 구성 화면의
// `toDisplayStep`) 코어의 형식기는 격자 밖 값을 거절한다. 세 번째 벌을 만들지 않으려고
// 이 모듈은 **밀리초 정수만** 내놓고 형식화는 화면이 그 둘 중 하나를 읽어 한다.

import type { EnemyRally, Roster } from '../../contract/schema.ts'
import {
  addDuration,
  type Duration,
  duration,
  type Instant,
  instant,
  toMillis,
} from '../../core/time/brand.ts'
import {
  compareByImpact,
  type EnemyAnchor,
  enemyImpact,
  planSquad,
  type SquadPlan,
} from '../../core/timing/defense.ts'

/**
 * 계약의 적 rally 한 줄을 코어가 받는 앵커로 옮긴다. **값을 바꾸지 않는다.**
 *
 * 옮기는 일 자체를 함수로 뺀 이유는 부르는 자리가 셋이기 때문이다(줄 목록·대응 계산·검사).
 * 세 곳이 각자 옮기면 그중 하나가 언젠가 단계를 빠뜨리고, 단계를 빠뜨린 앵커는 예외를 내지
 * 않고 **적 marching 하나만큼 어긋난 착탄**을 낸다.
 */
export function anchorOf(line: EnemyRally): EnemyAnchor {
  return {
    phase: line.phase,
    at: instant(line.anchoredAt),
    remaining: duration(line.remainingMs),
  }
}

/**
 * 명부에서 그 사람의 행군 시간을 꺼낸다. **아직 못 쟀으면 `undefined`.**
 *
 * `null` 을 `undefined` 로 접는 이유는 코어의 서명이 「없음」을 `undefined` 하나로 받기
 * 때문이다. 두 가지 「없음」이 화면 층을 흐르면 그중 하나가 언젠가 `0` 으로 접히고, 그
 * 순간 「아직 못 쟀다」와 「행군이 0초다」가 같은 값이 된다 — 명부가 계약 검증에서 실제로
 * 그 함정을 만났고(2026-08-11), 거기서는 착탄 미상이 통째로 사라졌다.
 */
export function marchingOf(rosters: readonly Roster[], rosterId: string): Duration | undefined {
  const found = rosters.find((row) => row.id === rosterId)
  if (found === undefined || found.marchingMs === null) return undefined
  return duration(found.marchingMs)
}

/** 지금 이 적 rally 가 화면에서 무엇을 흘리고 있는가. **앵커 단계와 다를 수 있다.** */
export type EnemyStage = 'rallying' | 'marching' | 'unknown'

export interface EnemyLine {
  rally: EnemyRally
  /** 명부에서 찾은 이름. **못 찾으면 null 이고 화면이 자기 말로 적는다** — 식별자를 뱉지 않는다. */
  enemyName: string | null
  /** 코어가 역산한 착탄. `undefined` 가 「착탄 미상」이다. */
  impact: Instant | undefined
  stage: EnemyStage
  /** 그 단계에서 흐르는 잔여. 미상이면 null. 형식화는 화면이 한다. */
  liveRemainingMs: number | null
}

/**
 * 지금 무엇이 흐르는가를 정한다. **rallying 이 0이 되면 저절로 행군으로 넘어간다** —
 * 2026-08-10 인게임 실측이 확인한 사실이고, 사람이 출발을 누르는 단계가 없으므로 화면도
 * 전이를 기다리지 않는다.
 *
 * 착탄 미상인 줄도 **rallying 이 남아 있는 동안에는 숫자가 흐른다.** 그 잔여는 앵커만으로
 * 정해져 명부와 무관하기 때문이다. 흐를 것이 없어지는 것은 rallying 이 끝난 뒤이고, 그때
 * 비로소 이 줄은 아무 시각도 주장하지 않는다.
 */
function stageNow(
  anchor: EnemyAnchor,
  impact: Instant | undefined,
  nowMs: number,
): { stage: EnemyStage; liveRemainingMs: number | null } {
  if (anchor.phase === 'rallying') {
    const rallyingEndsAt = toMillis(addDuration(anchor.at, anchor.remaining))
    if (nowMs < rallyingEndsAt) {
      return { stage: 'rallying', liveRemainingMs: rallyingEndsAt - nowMs }
    }
  }
  if (impact === undefined) return { stage: 'unknown', liveRemainingMs: null }
  return { stage: 'marching', liveRemainingMs: toMillis(impact) - nowMs }
}

/**
 * 적 rally 를 **등록한 순서 그대로** 세우고, 착탄 미상만 맨 아래 띠로 보낸다.
 *
 * 2026-08-26 사용자 결정이 2026-08-05 의 「계산된 착탄 순」을 뒤집었다. 다시 앵커하거나
 * 명부를 고쳐 착탄이 바뀌어도 **행의 자리는 바뀌지 않는다.** 목록이 다시 서면 CT 가 방금
 * 만진 행을 다시 찾아야 하고, 그 찾는 시간이 이 앱이 없애려는 지연이다. 추월(marching 이
 * 짧은 rally 가 먼저 닿는 것)은 사라진 것이 아니라 자리에서 숫자로 옮겨 갔다. 각 행의
 * 착탄이 그것을 말한다.
 *
 * 서버가 준 순서가 곧 등록 순서다(`createdAt` 오름차순으로 보낸다). 여기서 다시 정렬하지
 * 않는 이유는 순서의 정본이 두 곳에 살면 반드시 서로 다르게 낡기 때문이다.
 *
 * 착탄 미상은 여전히 맨 아래다(2026-08-15 사용자 결정). 이유가 바뀌었다. 착탄순에 놓을
 * 자리가 없어서가 아니라 **계산되지 않은 줄을 계산된 줄과 한 목록에 섞지 않으려는 것**이다.
 * 같은 무리 안에서는 등록 순서를 지킨다.
 */
export function enemyLines(
  enemyRallies: readonly EnemyRally[],
  rosters: readonly Roster[],
  nowMs: number,
): readonly EnemyLine[] {
  const lines = enemyRallies.map((rally): EnemyLine => {
    const anchor = anchorOf(rally)
    const impact = enemyImpact(anchor, marchingOf(rosters, rally.enemyRosterId))
    return {
      rally,
      enemyName: rosters.find((row) => row.id === rally.enemyRosterId)?.nickname ?? null,
      impact,
      ...stageNow(anchor, impact, nowMs),
    }
  })
  const known = lines.filter((line) => line.impact !== undefined)
  const unknown = lines.filter((line) => line.impact === undefined)
  return [...known, ...unknown]
}

/** 아군 한 명이 적 rally 하나에 대해 갖는 대응. **`plan` 은 코어의 판정 전량이다.** */
export interface SquadCell {
  enemyRallyId: string
  enemyName: string | null
  /**
   * 코어가 낸 판정을 **통째로** 들고 다닌다. 필요한 필드만 꺼내 새 객체로 옮기지 않는
   * 이유는, 그렇게 옮기는 순간 「무엇을 옮겼는가」가 이 파일의 판단이 되어 코어의 검사가
   * 닿지 못하는 두 번째 계약이 생기기 때문이다.
   */
  plan: SquadPlan
}

/**
 * 이 사람이 왜 흐린가. **화면이 고르지 않고 여기서 정한다** — 두 화면이 각자 고르면 같은
 * 사람이 한쪽에서는 「불가」, 다른 쪽에서는 「입력필요」로 뜬다.
 *
 * `all-impossible` 과 `nothing-actionable` 을 가른 것이 이 타입의 요점이다. 앞의 것은
 * **재 보고 전부 늦었다**는 주장이고 뒤의 것은 **아직 다 재지 못했다**는 사실이다. 착탄
 * 미상 줄이 하나라도 섞이면 앞의 주장은 과장이 된다 — 그 줄은 불가가 아니라 판정되지
 * 않은 것이고, 명부에 marching 이 들어오는 순간 가능으로 바뀔 수도 있다. **틀린 주장은
 * 아무 주장도 하지 않는 것보다 나쁘다**는 규칙이 여기에도 그대로 적용된다.
 */
export type DimReason = 'marching-missing' | 'all-impossible' | 'nothing-actionable'

export interface RosterSection {
  roster: Roster
  /** 행군을 아직 못 쟀으면 빈 배열이다 — 계산할 재료가 없다. */
  cells: readonly SquadCell[]
  /** 이 사람이 다음으로 보내야 하는 순간. 보낼 수 있는 대응이 하나도 없으면 null. */
  nextSendAt: Instant | null
  /**
   * **전 rally 불가.** 판정 계약이 이름으로 부르는 상태이고 홍길동이 그것이다.
   *
   * 줄 **전부가** 불가일 때만 참이다. 미상이 섞이면 거짓이 되고 사유는
   * `nothing-actionable` 이 된다 — 위 `DimReason` 주석에 왜 갈랐는지 있다.
   */
  allImpossible: boolean
  /** 아군 명부에 행군이 아직 없다. 계산이 아니라 입력이 빠진 것이라 사유가 다르다. */
  marchingMissing: boolean
  /** 흐리지 않으면 null. 흐리면 화면이 이 값으로 사유 한 줄을 고른다. */
  dimReason: DimReason | null
}

/**
 * 아군 roster 별 대응을 낸다. **섹션은 명부 순서 그대로, 한 사람 안의 줄은 착탄 순이다.**
 *
 * ── 섹션의 자리는 시간이 정하지 않는다 ─────────────────────────────────────
 *
 * 2026-08-25 사용자 결정이 2026-08-05 의 「급한 roster 부터, 전 rally 불가는 맨 아래」를
 * 뒤집었다. 그 규칙은 대응 하나가 지나가 불가로 바뀔 때마다 사람의 자리를 바꿨고, 연맹원은
 * 자기 이름을 매번 다시 찾았다. 지금은 서버가 준 명부 순서(닉네임 순)를 그대로 쓴다. 여기서
 * 다시 정렬하지 않는 이유는 명부 화면과 이 화면이 같은 순서여야 하기 때문이다. 순서의
 * 정본은 서버 하나다.
 *
 * ── 줄은 착탄 순이고, 지나간 줄도 자리를 지킨다 ────────────────────────────
 *
 * 한 사람 안에서 보낼 시점은 착탄에 상수를 더한 값이라 착탄 순과 보낼 순이 같다. 보낼
 * 시점이 지나 불가가 된 줄은 흐려질 뿐 아래로 내리지 않는다(2026-08-26 사용자가 열람
 * 화면에서 확인). 내리면 그것이 곧 시간에 따른 재정렬이고, 그
 * 자리에서 사람은 「무엇이 지나갔는가」를 잃는다. 착탄 미상은 코어의 비교기가 맨 뒤로
 * 보낸다. 그것은 시간이 아니라 입력이 바꾸는 자리라 재정렬이 아니다.
 *
 * ── 불가를 숨기지 않는 것이 계약이다 ───────────────────────────────────────
 *
 * 전 rally 에 대해 불가인 사람이 화면에서 사라지면 불통이다. 그 사람이 못 간다는 사실이
 * **CT 가 배정을 바꿀 근거**이기 때문이다. 그래서 이 함수는 아군을 한 명도 빼지 않고,
 * 흐리게 만들 근거(`allImpossible`, `marchingMissing`)만 함께 낸다.
 */
export function rosterSections(
  enemyRallies: readonly EnemyRally[],
  rosters: readonly Roster[],
  nowMs: number,
): readonly RosterSection[] {
  const now = instant(nowMs)
  const nameOf = (rosterId: string): string | null =>
    rosters.find((row) => row.id === rosterId)?.nickname ?? null

  const sections = rosters
    .filter((row) => row.side === 'ally')
    .map((ally): RosterSection => {
      const myMarching = marchingOf(rosters, ally.id)
      if (myMarching === undefined) {
        return {
          roster: ally,
          cells: [],
          nextSendAt: null,
          allImpossible: false,
          marchingMissing: true,
          dimReason: 'marching-missing',
        }
      }

      const cells = enemyRallies
        .map((rally): SquadCell => {
          const anchor = anchorOf(rally)
          return {
            enemyRallyId: rally.id,
            enemyName: nameOf(rally.enemyRosterId),
            plan: planSquad({
              anchor,
              enemyMarching: marchingOf(rosters, rally.enemyRosterId),
              myMarching,
              now,
            }),
          }
        })
        // 착탄순 하나뿐이다. 가능/불가로 무리를 가르지 않는다. 가르면 불가가 되는 순간 줄이
        // 아래로 내려가고, 그것이 곧 시간에 따른 재정렬이다(2026-08-25 사용자가 금지).
        .sort((a, b) =>
          compareByImpact(
            { impact: a.plan.status === 'impact-unknown' ? undefined : a.plan.impact },
            { impact: b.plan.status === 'impact-unknown' ? undefined : b.plan.impact },
          ),
        )

      const firstReady = cells.find((cell) => cell.plan.status === 'ready')
      const nextSendAt = firstReady?.plan.status === 'ready' ? firstReady.plan.sendAt : null
      const allImpossible =
        cells.length > 0 && cells.every((cell) => cell.plan.status === 'impossible')
      return {
        roster: ally,
        cells,
        nextSendAt,
        allImpossible,
        marchingMissing: false,
        dimReason:
          nextSendAt !== null ? null : allImpossible ? 'all-impossible' : 'nothing-actionable',
      }
    })

  // 명부 순서 그대로다. 정렬하지 않는 것이 이 함수의 계약이라 `sort` 가 여기 없다.
  return sections
}

/**
 * 상단 고정줄의 defense 아래층이 쓰는 두 값.
 *
 * **남은 시간은 다음 적 착탄까지 T− 다** — 수비에는 dispatch 가 없어 「내보내기 전에는
 * `T− --:--`」 규칙의 예외다. **요약은 적 rally 개수 하나이고 착탄 미상을 합산한다** —
 * 미상 행을 맨 아래 흐린 띠로 내리는 대가를 여기서 갚는 자리라, 합산에서 빼면 그 rally 가
 * 화면에서 두 번 밀려난다.
 *
 * 두 화면이 이 함수를 함께 부른다. 각자 세면 같은 방의 두 화면이 다른 개수를 말하고, 그때
 * 사람은 둘 중 무엇을 믿을지 고르게 된다.
 */
export function defenseBand(
  lines: readonly EnemyLine[],
  nowMs: number,
): { nextImpactInMs: number | null; count: number } {
  const upcoming = lines
    .filter((line) => line.impact !== undefined && toMillis(line.impact) > nowMs)
    .map((line) => toMillis(line.impact as Instant))
    .sort((a, b) => a - b)[0]
  return {
    nextImpactInMs: upcoming === undefined ? null : upcoming - nowMs,
    count: lines.length,
  }
}
